import Close from "@/assets/svg/16/Close";
import clsx from "clsx";
import { forwardRef, useId } from "react";
import styles from "./index.module.css";

export interface TextFieldProps
	extends Omit<React.ComponentProps<"input">, "size"> {
	label?: string;
	placeholder?: string;
	icon?: React.ReactNode;

	size?: "large" | "medium";
	variant?: "outlined" | "filled";

	classNames?: {
		container?: string;
		input?: string;
		icon?: string;
	};
	error?: string | null;
	clearIcon?: boolean;
	onClear?: () => void;
}

const sizes = {
	large: "",
	medium: styles.medium,
};

const variants = {
	outlined: "",
	filled: styles.filled,
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
	(
		{
			label,
			size,
			placeholder,
			classNames,
			className,
			id,
			icon,
			variant,
			error,
			clearIcon,
			onClear,
			...inputProps
		},
		ref,
	) => {
		const internalId = useId();
		const resolvedId = id || internalId;

		return (
			<label
				htmlFor={resolvedId}
				className={clsx(
					styles.textField,
					size && sizes[size],
					!!error && styles.error,
					className,
					variant && variants[variant],
				)}
			>
				{label && <span className={styles.labelText}>{error ?? label}</span>}
				<input
					ref={ref}
					id={resolvedId}
					type="text"
					className={clsx(styles.input, classNames?.input)}
					placeholder={placeholder}
					{...inputProps}
				/>
				{icon && (
					<span className={clsx(styles.icon, classNames?.icon)}>{icon}</span>
				)}
				{clearIcon && (
					<Close
						className={styles.clearIcon}
						onClick={() => {
							onClear?.();
						}}
					/>
				)}
			</label>
		);
	},
);

TextField.displayName = "TextField";
